// The surface of one tide pool. The pen grid is uploaded as a float texture
// (lit / dark / wake times per cell) and the shader turns it into plankton
// light, creeping ink and glowing wakes. Everything snaps to 32 texels per
// unit so it sits with the pixel-art environment.
import * as THREE from 'three';
import { CELL, LIT, OPEN } from '../sim/grid.js';
import { ONE } from '../sim/num.js';
import { KAGEE_WATER_FS, blurMasks, nightGels, SCREENS, MODE_INDEX } from './water-kagee.js';
import { HeightField } from './ripples.js';

// Keyed to the shells the player reads: sazae green, triton coral, cowrie amber, moon-snail lilac.
export const CRAB_TINTS = [new THREE.Color('#9fd16a'), new THREE.Color('#ff8a5c'), new THREE.Color('#ffc23a'), new THREE.Color('#b9a2ff')];

const vs = /* glsl */`
varying vec2 vGrid; varying vec3 vWorld;
uniform vec4 gridBox; // x0, y0 (units), width, height (units)
void main() {
  vec4 w = modelMatrix * vec4(position, 1.0);
  vWorld = w.xyz;
  vGrid = (position.xz - gridBox.xy) / gridBox.zw;
  gl_Position = projectionMatrix * viewMatrix * w;
}`;

const fs = /* glsl */`
uniform sampler2D cells; uniform sampler2D mask; uniform vec2 gridSize; uniform vec4 gridBox;
uniform float now; uniform vec3 tints[4]; uniform float moon; uniform vec3 moonDir;
uniform vec4 ripples[12]; uniform float calm; uniform vec3 deep; uniform vec3 shallow; uniform vec2 origin;
uniform float kagee; uniform sampler2D pens; uniform vec3 penColors[17]; uniform vec2 flow;
uniform sampler2D stencil; uniform float hasStencil; uniform float pattern;
float stencilAt(vec2 c) { return texture2D(stencil, (c + 0.5) / gridSize).r; }
varying vec2 vGrid; varying vec3 vWorld;
float penAt(vec2 g) { return floor(texture2D(pens, (floor(g + 0.5) + 0.5) / gridSize).r * 255.0 + 0.5); }

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), f.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), f.x), f.y);
}
vec4 cellAt(vec2 c) { return texture2D(cells, (c + 0.5) / gridSize); }

// Presence of each layer at one cell, animated by the times stored in it.
vec4 layers(vec4 t, float jitter) {
  float lit = t.r >= 0.0 ? smoothstep(t.r, t.r + 0.18, now) : 0.0;
  float dark = t.g >= 0.0 ? smoothstep(t.g - 0.1, t.g + 0.55, now + jitter * 0.35) : 0.0;
  float flags = t.a;
  float wakeOn = step(7.5, mod(flags, 16.0));
  float wake = t.b >= 0.0 ? (wakeOn > 0.5 ? 0.35 + 0.65 * exp(-(now - t.b) * 1.6) : 0.0) : 0.0;
  if (t.b >= 0.0 && now < t.b) wake = 0.0;
  float forecast = step(3.5, mod(flags, 8.0));
  return vec4(lit, dark, wake, forecast);
}

void main() {
  // Snap to the 32-texel world grid (HD-2D); the paper-cut look keeps smooth curves.
  vec2 wp = kagee > 0.5 ? vWorld.xz : floor(vWorld.xz * 32.0) / 32.0 + 1.0 / 64.0;
  vec2 lp = wp - origin;
  vec2 g = (lp - gridBox.xy) / gridBox.zw * gridSize - 0.5;
  float m = texture2D(mask, (lp - gridBox.xy) / gridBox.zw).r;
  if (m < 0.5) discard;
  vec2 c0 = floor(g); vec2 f = g - c0;
  float jitter = noise(wp * 3.0 + now * 0.3);
  vec4 a = layers(cellAt(c0), jitter), b = layers(cellAt(c0 + vec2(1, 0)), jitter);
  vec4 c = layers(cellAt(c0 + vec2(0, 1)), jitter), d = layers(cellAt(c0 + vec2(1, 1)), jitter);
  vec4 L = mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  // Wake owner colour from the nearest cell.
  vec4 near = cellAt(floor(g + 0.5));
  vec3 tint = tints[int(mod(near.a, 4.0))];

  // Base water: dark, moonlit, faint caustics.
  float caust = pow(noise(wp * 1.7 + vec2(now * 0.21, -now * 0.17)) * noise(wp * 2.3 - vec2(now * 0.13, now * 0.19)), 1.6);
  vec3 col = mix(deep, shallow, 0.35 + 0.5 * caust * (0.3 + moon));
  float alpha = 0.42;

  // Ripples from impacts.
  float rip = 0.0;
  for (int i = 0; i < 12; i++) {
    vec4 r = ripples[i];
    float age = now - r.z;
    if (age < 0.0 || age > 1.6) continue;
    float dist = length(wp - r.xy);
    float ring = abs(dist - age * 2.6);
    rip += r.w * smoothstep(0.16, 0.0, ring) * (1.0 - age / 1.6);
  }
  col += vec3(0.55, 0.75, 1.0) * rip * 0.5;
  // Current: faint dashes drifting with the flow.
  float flowLines = 0.0;
  if (dot(flow, flow) > 0.0) {
    vec2 fd = normalize(flow), fn = vec2(-fd.y, fd.x);
    float along = dot(wp, fd) * 1.4 - now * 1.1, across = dot(wp, fn) * 3.0;
    flowLines = step(0.82, fract(along + floor(across) * 0.37)) * step(0.55, fract(across)) * 0.5;
  }
  col += vec3(0.5, 0.75, 1.0) * flowLines * 0.25;

  // Moon glints: sparse pixels that twinkle.
  float tw = step(0.9985, hash(floor(wp * 32.0) + floor(now * 2.0)));
  col += vec3(0.9, 0.95, 1.0) * tw * 0.25 * moon;

  // Dark undertow: ink with a violet edge and slow tendrils.
  float swirl = noise(wp * 2.2 + vec2(now * 0.25, -now * 0.18)) - 0.5;
  float ink = smoothstep(0.25, 0.5, L.g + swirl * 0.3);
  float edge = smoothstep(0.12, 0.3, L.g + swirl * 0.15) * (1.0 - smoothstep(0.3, 0.48, L.g + swirl * 0.15));
  col = mix(col, vec3(0.006, 0.005, 0.014), ink);
  // Slow, soft violet currents inside the ink so it reads as moving water, not a hole.
  float cur = noise(wp * 0.6 + vec2(-now * 0.08, now * 0.06));
  col += vec3(0.035, 0.012, 0.07) * smoothstep(0.55, 0.85, cur) * ink;
  col += vec3(0.30, 0.13, 0.58) * edge;
  alpha = mix(alpha, 0.97, max(ink, edge * 0.8));

  // Telegraph: next turn's dark as a slow diagonal hatch of shadow (not light).
  float hatch = step(0.55, fract((wp.x + wp.y) * 2.0 - now * 0.35));
  col = mix(col, col * 0.35 + vec3(0.03, 0.0, 0.06), L.a * hatch * (1.0 - ink));
  alpha = mix(alpha, 0.85, L.a * hatch * (1.0 - ink));

  // Wakes: a faint plankton haze over the cells that block the undertow
  // (the bright line itself is a ribbon mesh drawn by the view).
  float spark = step(0.93, hash(floor(wp * 32.0) + floor(now * 7.0)));
  vec3 wakeCol = mix(tint, vec3(0.30, 0.95, 1.0), 0.15);
  col += wakeCol * L.b * (0.16 + spark * 0.35) * (1.0 - ink);

  // Lit water turns into a clear, shallow lagoon: brighter teal, caustics, rare
  // sparkles. A thin bright wavefront runs through it as it fills.
  float glow = L.r;
  float front = 0.0;
  { vec4 t = near; if (t.r >= 0.0 && now >= t.r) front = exp(-(now - t.r) * 9.0); }
  float lagoonCaust = pow(caust, 0.7);
  vec3 lagoon = mix(vec3(0.05, 0.24, 0.30), vec3(0.16, 0.46, 0.50), lagoonCaust);
  float twinkle = step(0.975, hash(floor(wp * 32.0) + floor(now * 2.5 + hash(floor(wp * 4.0)) * 5.0)));
  col = mix(col, lagoon, glow * 0.8);
  col += vec3(0.55, 1.1, 1.15) * glow * twinkle * 0.25;
  col += vec3(0.40, 0.95, 1.0) * front * 0.75;
  alpha = mix(alpha, 0.6, glow);

  col += vec3(0.08, 0.3, 0.36) * calm;
  // Picture nights: the motif as a faint gold dotted outline; lit parts turn gold.
  float st = 0.0, stEdge = 0.0;
  if (hasStencil > 0.5) {
    float a0 = stencilAt(c0), a1 = stencilAt(c0 + vec2(1, 0)), a2 = stencilAt(c0 + vec2(0, 1)), a3 = stencilAt(c0 + vec2(1, 1));
    st = mix(mix(a0, a1, f.x), mix(a2, a3, f.x), f.y);
    stEdge = smoothstep(0.25, 0.5, st) * (1.0 - smoothstep(0.5, 0.75, st));
    float dots = step(0.5, fract((wp.x + wp.y) * 5.0 + now * 0.4));
    col += vec3(1.0, 0.82, 0.4) * stEdge * dots * 0.55 * (1.0 - L.r) + vec3(0.25, 0.2, 0.08) * smoothstep(0.5, 0.8, st) * (1.0 - L.r) * 0.25;
    col = mix(col, mix(vec3(0.08, 0.14, 0.32), vec3(0.95, 0.72, 0.28), smoothstep(0.5, 0.8, st)), L.r * 0.75);
  }
  if (kagee > 0.5) {
    // 影絵: the pool is a light box of blue cellophane lit from below.
    // Cellophane: broad, slow density changes plus fine long fibres, no blotches.
    float sheet = noise(wp * 0.35 + vec2(now * 0.02, 0.0)) * 0.6 + noise(wp * 0.9 - now * 0.03) * 0.4;
    float fibre = noise(wp * vec2(14.0, 1.2)) * noise(wp * vec2(1.1, 13.0));
    vec3 kc = mix(vec3(0.014, 0.055, 0.28), vec3(0.04, 0.12, 0.46), sheet) * (0.92 + 0.16 * fibre);
    // Brighter toward the middle of the pool, like a lamp under the sheet.
    vec2 rel = (lp - gridBox.xy) / gridBox.zw - 0.5;
    kc *= 1.25 - dot(rel, rel) * 1.6;
    kc += vec3(0.5, 0.7, 1.0) * rip * 0.35 + vec3(0.25, 0.4, 0.9) * flowLines * 0.25;
    // Light pens become stained glass in the colours of the crabs who closed them.
    // Smooth the cell field with a 3x3 tent so the glass edges are paper-cut curves, not steps.
    float sm = 0.0, sw = 0.0;
    vec3 glass = vec3(0.0); float gw = 0.0;
    // Top two glass pieces by weight, for curved lead lines between them.
    float idA = -1.0, wA = 0.0, idB = -1.0, wB = 0.0;
    for (int yy = -1; yy <= 1; yy++) for (int xx = -1; xx <= 1; xx++) {
      vec2 cc = c0 + vec2(float(xx), float(yy)) + 0.5;
      vec2 dd = (g + 0.5) - (cc + 0.0) - 0.5 + 0.5;
      float wgt = max(0.0, 1.5 - abs(g.x - (c0.x + float(xx)))) * max(0.0, 1.5 - abs(g.y - (c0.y + float(yy))));
      vec4 tc = cellAt(c0 + vec2(float(xx), float(yy)));
      float litc = tc.r >= 0.0 ? smoothstep(tc.r, tc.r + 0.18, now) : 0.0;
      sm += litc * wgt; sw += wgt;
      float pmc = floor(texture2D(pens, (c0 + vec2(float(xx), float(yy)) + 0.5) / gridSize).r * 255.0 + 0.5);
      glass += penColors[int(pmc)] * litc * wgt; gw += litc * wgt;
      float wl = litc * wgt;
      if (wl > 0.0) {
        if (pmc == idA) wA += wl; else if (pmc == idB) wB += wl;
        else if (idA < 0.0) { idA = pmc; wA = wl; } else if (idB < 0.0) { idB = pmc; wB = wl; }
      }
    }
    if (wB > wA) { float t1 = wA; wA = wB; wB = t1; }
    float seam = wB > 0.0 ? smoothstep(0.28, 0.0, (wA - wB) / (wA + wB)) : 0.0;
    glow = smoothstep(0.35, 0.65, sm / sw);
    glass = gw > 0.0 ? glass / gw : penColors[0];
    float pm = penAt(g);
    // Hand-rolled glass: mottled density, fine drawn streaks, a few seeds (bubbles).
    float mott = 0.7 + 0.45 * noise(wp * 2.3 + pm * 3.1) + 0.15 * noise(wp * vec2(9.0, 1.4) + pm);
    float seed = step(0.985, hash(floor(wp * 18.0) + pm)) * 0.6;
    // A little of the blue lamp still shows through the glass.
    vec3 lamp = kc;
    // Picture nights: lit parts of the motif are gold glass.
    vec3 gold = vec3(0.75, 0.48, 0.1) * (0.85 + 0.3 * mott);
    if (hasStencil > 0.5) glass = mix(vec3(0.06, 0.1, 0.32), gold, smoothstep(0.5, 0.8, st));
    // Glass pattern the player chose (looks only): cut facets, tie-dye rings, gold flecks.
    if (pattern > 0.5 && pattern < 1.5) { float f2 = abs(fract((wp.x + wp.y) * 2.5) - 0.5) + abs(fract((wp.x - wp.y) * 2.5) - 0.5); glass *= 0.75 + 0.6 * step(0.66, f2); }
    if (pattern > 1.5 && pattern < 2.5) { vec2 q = fract(wp * 1.6) - 0.5; glass = mix(glass, glass * 0.4 + vec3(0.5), smoothstep(0.06, 0.0, abs(length(q) - 0.28)) * 0.7); }
    if (pattern > 2.5) glass += vec3(0.8, 0.6, 0.2) * step(0.985, hash(floor(wp * 22.0))) * 0.8;
    kc = mix(kc, glass * mott * (0.9 + 0.35 * caust) + glass * seed + lamp * 0.18, glow);
    float dots2 = step(0.5, fract((wp.x + wp.y) * 5.0 + now * 0.4));
    kc += vec3(1.0, 0.8, 0.35) * stEdge * dots2 * 0.6 * (1.0 - glow);
    // Lead came: a thin black line round each pen's edge.
    float lead = max(smoothstep(0.22, 0.0, abs(glow - 0.5)) * step(0.01, glow), seam * glow);
    kc = mix(kc, vec3(0.012, 0.01, 0.02), lead * 0.92);
    kc += vec3(1.0, 0.95, 0.8) * front * 0.8;
    kc += wakeCol * L.b * 0.3 * (1.0 - ink);
    // The undertow is black paper sliding over the light.
    kc = mix(kc, vec3(0.004, 0.003, 0.008), ink);
    kc += vec3(0.16, 0.08, 0.36) * edge * 0.6;
    kc = mix(kc, kc * 0.22, L.a * hatch * (1.0 - ink));
    gl_FragColor = vec4(kc, 0.97);
    return;
  }
  gl_FragColor = vec4(col, alpha);
}`;

export class PoolWater {
  constructor(pool, centre, opts = {}) {
    const g = pool.grid;
    this.g = g;
    this.data = new Float32Array(g.w * g.h * 4).fill(-1);
    for (let i = 0; i < g.w * g.h; i++) this.data[i * 4 + 3] = 0;
    this.tex = new THREE.DataTexture(this.data, g.w, g.h, THREE.RGBAFormat, THREE.FloatType);
    this.tex.magFilter = this.tex.minFilter = THREE.NearestFilter;
    this.tex.needsUpdate = true;
    this.penData = new Uint8Array(g.w * g.h);
    this.penTex = new THREE.DataTexture(this.penData, g.w, g.h, THREE.RedFormat, THREE.UnsignedByteType);
    this.penTex.magFilter = this.penTex.minFilter = THREE.NearestFilter;
    this.penTex.needsUpdate = true;
    const st = new Uint8Array(g.w * g.h);
    if (pool.stencil) for (const i of pool.stencil.cells) st[i] = 255;
    this.stencilTex = new THREE.DataTexture(st, g.w, g.h, THREE.RedFormat, THREE.UnsignedByteType);
    this.stencilTex.magFilter = this.stencilTex.minFilter = THREE.NearestFilter;
    this.stencilTex.needsUpdate = true;
    const box = new THREE.Vector4(g.x0 / ONE, g.y0 / ONE, (g.w * CELL) / ONE, (g.h * CELL) / ONE);
    this.mask = polygonMask(pool.poly, box);
    this.ripples = Array.from({ length: 12 }, () => new THREE.Vector4(0, 0, -99, 0));
    this.rippleNext = 0;
    const kagee = !!PoolWater.kagee;
    this.kagee = kagee;
    if (kagee) {
      // Smoothed shape masks (lit, dark, picture, forecast), rebuilt when dirty.
      const n = g.w * g.h;
      this.masks = [new Uint8Array(n), new Uint8Array(n), st.map(v => (v ? 1 : 0)), new Uint8Array(n)];
      this.fieldData = new Uint8Array(n * 4);
      this.field = new THREE.DataTexture(this.fieldData, g.w, g.h, THREE.RGBAFormat, THREE.UnsignedByteType);
      this.field.magFilter = this.field.minFilter = THREE.LinearFilter;
      // One smoothed mask per gel (8 gels over two RGBA textures).
      this.gelCells = Array.from({ length: 8 }, () => new Uint8Array(n));
      this.gelTex = [0, 1].map(() => {
        const t = new THREE.DataTexture(new Uint8Array(n * 4), g.w, g.h, THREE.RGBAFormat, THREE.UnsignedByteType);
        t.magFilter = t.minFilter = THREE.LinearFilter;
        return t;
      });
      this.dirty = true;
      // The moving surface: a height field at twice the grid's resolution;
      // the rim (outside) and rocks are walls the waves bounce off.
      const fw = g.w * 2, fh = g.h * 2, wall = new Uint8Array(fw * fh);
      for (let y = 0; y < fh; y++) for (let x = 0; x < fw; x++) {
        const k = g.kind[(x >> 1) + (y >> 1) * g.w];
        wall[x + y * fw] = k === OPEN || k === LIT ? 0 : 1;
      }
      this.surf = new HeightField(fw, fh, wall);
      this.surfTex = new THREE.DataTexture(new Uint8Array(fw * fh), fw, fh, THREE.RedFormat, THREE.UnsignedByteType);
      this.surfTex.magFilter = this.surfTex.minFilter = THREE.LinearFilter;
      this.surfBox = box;
      this.surfT = null; this.windT = 0;
    }
    const screen = SCREENS[pool.mode] || SCREENS.pen;
    this.material = new THREE.ShaderMaterial({
      vertexShader: vs, fragmentShader: kagee ? KAGEE_WATER_FS : fs, transparent: true, depthWrite: false,
      uniforms: {
        cells: { value: this.tex }, mask: { value: this.mask }, gridSize: { value: new THREE.Vector2(g.w, g.h) },
        gridBox: { value: box }, now: { value: 0 }, tints: { value: CRAB_TINTS }, moon: { value: 0.5 },
        moonDir: { value: new THREE.Vector3(0, 1, -1) }, ripples: { value: this.ripples }, calm: { value: 0 },
        origin: { value: new THREE.Vector2(centre[0], centre[1]) },
        deep: { value: new THREE.Color('#0b2236') }, shallow: { value: new THREE.Color('#1f5568') },
        kagee: { value: PoolWater.kagee ? 1 : 0 }, pens: { value: this.penTex }, penColors: { value: opts.tints ? penColorsFor(opts.tints) : PEN_COLORS },
        stencil: { value: this.stencilTex }, hasStencil: { value: pool.stencil ? 1 : 0 }, pattern: { value: { kiriko: 1, shibori: 2, kinran: 3 }[opts.pattern] || 0 },
        field: { value: this.field || null }, surf: { value: this.surfTex || null }, surfTexel: { value: new THREE.Vector2(1 / (g.w * 2), 1 / (g.h * 2)) }, gelA: { value: this.gelTex?.[0] || null }, gelB: { value: this.gelTex?.[1] || null }, gels: { value: nightGels(opts.phase ?? 4, (opts.tints || CRAB_TINTS).map(c => '#' + c.getHexString())) },
        screenA: { value: new THREE.Color(screen[0]) }, screenB: { value: new THREE.Color(screen[1]) }, mode: { value: MODE_INDEX[pool.mode] || 0 },
        flow: { value: new THREE.Vector2(pool.rules?.current ? pool.rules.current[0] / 1000 : 0, pool.rules?.current ? pool.rules.current[1] / 1000 : 0) },
      },
    });
    const geo = new THREE.PlaneGeometry(box.z, box.w, 1, 1);
    geo.rotateX(-Math.PI / 2);
    geo.translate(box.x + box.z / 2, 0, box.y + box.w / 2);
    this.mesh = new THREE.Mesh(geo, this.material);
    this.mesh.position.set(centre[0], 0, centre[1]);
    this.mesh.renderOrder = 2;
    this.syncFromGrid(pool, -10, true);
  }

  cellWorld(i) {
    const g = this.g;
    return [(g.x0 + (i % g.w) * CELL + CELL / 2) / ONE + this.mesh.position.x, (g.y0 + Math.floor(i / g.w) * CELL + CELL / 2) / ONE + this.mesh.position.z];
  }

  // Bring the texture in line with the sim grid at `now` (used at pool start
  // and after a turn ends, so nothing drifts from the truth).
  syncFromGrid(pool, now, instant = false) {
    const g = pool.grid, d = this.data;
    for (let i = 0; i < g.w * g.h; i++) {
      const k = i * 4;
      if (g.kind[i] === LIT && d[k] < 0) d[k] = instant ? now : now;
      if (g.kind[i] !== LIT) d[k] = -1;
      if (g.dark[i] && d[k + 1] < 0) d[k + 1] = instant ? now - 5 : now;
      if (!g.dark[i]) d[k + 1] = -1;
      // Blocking during the coming turn (rain washes last turn's wakes away).
      const active = g.wake[i] > 0 && g.wake[i] >= (pool.rules?.rain ? pool.turn + 1 : pool.turn);
      const owner = ownerIndex(g.owner[i]);
      if (active && d[k + 2] < 0) d[k + 2] = now - 2;
      if (!active && g.wake[i] > 0) d[k + 2] = -1;
      d[k + 3] = owner + (active ? 8 : 0) + (d[k + 3] % 8 >= 4 ? 4 : 0);
      if (this.kagee) {
        this.masks[0][i] = g.kind[i] === LIT ? 1 : 0;
        this.masks[1][i] = g.dark[i] ? 1 : 0;
        if (g.kind[i] === LIT && !this.penData[i]) this.penData[i] = g.pen[i] === 16 ? 6 : g.penBy[i];
      }
    }
    this.tex.needsUpdate = true;
    this.penTex.needsUpdate = true;
    if (this.kagee) this.dirty = true;
  }

  paintWake(i, owner, at) {
    const k = i * 4;
    this.data[k + 2] = at;
    this.data[k + 3] = ownerIndex(owner) + 8 + (this.data[k + 3] % 8 >= 4 ? 4 : 0);
    this.tex.needsUpdate = true;
  }
  // `gel` (影絵 only): 1 + the crab that closed the pen, 5 all four, 6 the moon.
  light(cells, at, step = 0.004, crabs = 0, gel = 0) {
    const id = this.kagee ? (crabs === 16 ? 6 : gel) : crabs;
    // 影絵: the whole gel slides in at once; a cell-by-cell sweep would show the grid.
    if (this.kagee) step = 0;
    cells.forEach((i, n) => { this.data[i * 4] = at + n * step; this.data[i * 4 + 1] = -1; this.penData[i] = id; });
    if (this.kagee) { for (const i of cells) { this.masks[0][i] = 1; this.masks[1][i] = 0; } this.dirty = true; }
    this.tex.needsUpdate = true;
    this.penTex.needsUpdate = true;
  }
  eraseWake(cells) {
    for (const i of cells) { this.data[i * 4 + 2] = -1; this.data[i * 4 + 3] = this.data[i * 4 + 3] % 8; }
    this.tex.needsUpdate = true;
  }
  darken(cells, at) {
    for (const i of cells) this.data[i * 4 + 1] = at;
    if (this.kagee) { for (const i of cells) this.masks[1][i] = 1; this.dirty = true; }
    this.tex.needsUpdate = true;
  }
  forecast(cells) {
    const d = this.data;
    for (let i = 0; i < d.length / 4; i++) if (d[i * 4 + 3] % 8 >= 4) d[i * 4 + 3] -= 4;
    for (const i of cells) d[i * 4 + 3] += 4;
    if (this.kagee) { this.masks[3].fill(0); for (const i of cells) this.masks[3][i] = 1; this.dirty = true; }
    this.tex.needsUpdate = true;
  }
  ripple(x, z, at, strength = 1) {
    this.ripples[this.rippleNext].set(x, z, at, strength);
    this.rippleNext = (this.rippleNext + 1) % this.ripples.length;
    this.stir(x, z, 0.25 + 0.2 * strength, 0.12 * strength);
  }
  // Push the moving surface at a world point (radius in world units).
  stir(x, z, r, amount) {
    if (!this.surf) return;
    const [fx, fz] = this.surfCell(x, z);
    this.surf.disturb(fx, fz, r * 8, amount);   // 8 field cells per unit
  }
  surfCell(x, z) {
    const b = this.surfBox;
    return [((x - this.mesh.position.x - b.x) / b.z) * this.surf.w - 0.5, ((z - this.mesh.position.z - b.y) / b.w) * this.surf.hgt - 0.5];
  }
  // Height and slope of the surface under a world point, for things that float.
  surfAt(x, z) {
    if (!this.surf) return { h: 0, sx: 0, sz: 0 };
    const [fx, fz] = this.surfCell(x, z);
    const [sx, sz] = this.surf.slope(fx, fz);
    return { h: this.surf.height(fx, fz), sx, sz };
  }
  update(now, moon) {
    this.material.uniforms.now.value = now; this.material.uniforms.moon.value = moon;
    if (this.surf) {
      // Fixed 60 Hz steps from presentation time (so pause/advance stay exact), at most 8 a frame.
      if (this.surfT == null || now < this.surfT) this.surfT = now;
      let n = 0;
      while (this.surfT + 1 / 60 <= now && n < 8) {
        this.surfT += 1 / 60; n++;
        // A breath of wind now and then, so still water is never dead.
        if ((this.windT += 1 / 60) > 0.6) { this.windT = 0; const s = this.surf; s.disturb(Math.random() * s.w, Math.random() * s.hgt, 4 + Math.random() * 4, 0.04); }
        this.surf.step();
      }
      if (n) { this.surf.encode(this.surfTex.image.data); this.surfTex.needsUpdate = true; }
      if (now - this.surfT > 0.5) this.surfT = now;
    }
    if (this.dirty) {
      const g = this.g;
      blurMasks(g.w, g.h, [this.masks[0], this.masks[1], this.masks[2], this.masks[3]], this.fieldData);
      const gc = this.gelCells;
      for (const c of gc) c.fill(0);
      for (let i = 0; i < g.w * g.h; i++) if (this.masks[0][i]) gc[Math.min(7, this.penData[i])][i] = 1;
      blurMasks(g.w, g.h, gc.slice(0, 4), this.gelTex[0].image.data);
      blurMasks(g.w, g.h, gc.slice(4), this.gelTex[1].image.data);
      this.field.needsUpdate = true;
      for (const t of this.gelTex) t.needsUpdate = true;
      this.dirty = false;
    }
  }
  dispose() { this.field?.dispose(); this.surfTex?.dispose(); this.gelTex?.forEach(t => t.dispose()); this.mesh.geometry.dispose(); this.material.dispose(); this.tex.dispose(); this.mask.dispose(); this.penTex.dispose(); this.stencilTex.dispose(); }
}

// Glass colour per pen: the mix of the crabs whose wakes closed it (index =
// crab bitmask), saturated like cellophane; 0 = pools lit by rocks alone, 16 = the moon (calmed nushi).
// Crab colours come from the shells they wear, so a shell swap changes the glass.
export function penColorsFor(tints = CRAB_TINTS) { return Array.from({ length: 17 }, (_, m) => {
  if (m === 0) return new THREE.Color('#1f8f86');
  if (m === 16) return new THREE.Color('#c9c29a');
  const c = new THREE.Color(0, 0, 0); let n = 0;
  for (let b = 0; b < 4; b++) if (m & (1 << b)) { c.add(tints[b]); n++; }
  c.multiplyScalar(1 / n);
  const hsl = {}; c.getHSL(hsl);
  // Pick in display space so the glass stays saturated and mid-bright after tone mapping.
  c.getHSL(hsl, THREE.SRGBColorSpace);
  return new THREE.Color().setHSL(hsl.h, Math.min(0.7, hsl.s * 0.75), 0.42, THREE.SRGBColorSpace);
}); }
const PEN_COLORS = penColorsFor();

const ownerIndex = bits => (bits & 1 ? 0 : bits & 2 ? 1 : bits & 4 ? 2 : bits & 8 ? 3 : 0);

function polygonMask(poly, box) {
  const ppu = 16;
  const w = Math.ceil(box.z * ppu), h = Math.ceil(box.w * ppu);
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const ctx = cv.getContext('2d');
  ctx.fillStyle = '#000'; ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  // Grow the outline slightly so the water tucks under the rim rocks.
  poly.forEach(([x, y], k) => {
    const px = (x / ONE - box.x) * ppu, py = (y / ONE - box.y) * ppu;
    k ? ctx.lineTo(px, py) : ctx.moveTo(px, py);
  });
  ctx.closePath(); ctx.fill();
  ctx.lineWidth = ppu * 0.5; ctx.strokeStyle = '#fff'; ctx.stroke();
  const t = new THREE.CanvasTexture(cv);
  t.flipY = false;
  t.magFilter = t.minFilter = THREE.LinearFilter;
  return t;
}

export { OPEN };
